import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

type MetricKey = 'pv' | 'uv';

interface DayData {
  date: string;
  pv: number;
  uv: number;
}

const metrics: { key: MetricKey; label: string; color: string; bg: string }[] = [
  { key: 'pv', label: 'PV', color: '#0d9488', bg: 'rgba(13,148,136,0.12)' },
  { key: 'uv', label: 'UV', color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
];

export default function VisitTrendChart() {
  const [activeMetrics, setActiveMetrics] = useState<MetricKey[]>(['pv', 'uv']);
  const [trendData, setTrendData] = useState<DayData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrend = async () => {
      try {
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
        const { data } = await supabase
          .from('site_visits')
          .select('created_at, session_id')
          .gte('created_at', thirtyDaysAgo)
          .order('created_at', { ascending: true });

        if (!data || data.length === 0) {
          // 无数据时生成空的30天框架
          const days: DayData[] = [];
          for (let i = 29; i >= 0; i--) {
            const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
            days.push({
              date: `${d.getMonth() + 1}/${d.getDate()}`,
              pv: 0,
              uv: 0,
            });
          }
          setTrendData(days);
          return;
        }

        // 按天聚合
        const dayMap: Record<string, { pv: number; sessions: Set<string> }> = {};
        data.forEach((row) => {
          const d = new Date(row.created_at);
          const key = `${d.getMonth() + 1}/${d.getDate()}`;
          if (!dayMap[key]) dayMap[key] = { pv: 0, sessions: new Set() };
          dayMap[key].pv += 1;
          if (row.session_id) dayMap[key].sessions.add(row.session_id);
        });

        const days: DayData[] = [];
        for (let i = 29; i >= 0; i--) {
          const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
          const key = `${d.getMonth() + 1}/${d.getDate()}`;
          days.push({
            date: key,
            pv: dayMap[key]?.pv || 0,
            uv: dayMap[key]?.sessions.size || 0,
          });
        }
        setTrendData(days);
      } catch (err) {
        console.error('Failed to fetch trend:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTrend();
  }, []);

  const toggleMetric = (key: MetricKey) => {
    setActiveMetrics((prev) =>
      prev.includes(key)
        ? prev.length > 1 ? prev.filter((k) => k !== key) : prev
        : [...prev, key]
    );
  };

  const maxVal = trendData.length > 0
    ? Math.max(...trendData.flatMap((d) => activeMetrics.map((m) => d[m])), 1)
    : 1;

  const chartH = 200;
  const chartW = 800;
  const padL = 48;
  const padR = 16;
  const padT = 16;
  const padB = 32;
  const innerW = chartW - padL - padR;
  const innerH = chartH - padT - padB;
  const n = trendData.length || 1;

  const getX = (i: number) => padL + (i / Math.max(n - 1, 1)) * innerW;
  const getY = (val: number) => padT + innerH - (val / maxVal) * innerH;

  const buildPath = (key: MetricKey) => {
    if (trendData.length === 0) return '';
    const pts = trendData.map((d, i) => `${getX(i)},${getY(d[key])}`);
    return `M ${pts.join(' L ')}`;
  };

  const buildArea = (key: MetricKey) => {
    if (trendData.length === 0) return '';
    const pts = trendData.map((d, i) => `${getX(i)},${getY(d[key])}`);
    const bottom = padT + innerH;
    return `M ${getX(0)},${bottom} L ${pts.join(' L ')} L ${getX(n - 1)},${bottom} Z`;
  };

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((r) => Math.round(maxVal * r));

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">访问趋势（近30天）</h3>
          <p className="text-xs text-gray-400 mt-0.5">基于真实访问记录统计</p>
        </div>
        <div className="flex items-center gap-2">
          {metrics.map((m) => (
            <button
              key={m.key}
              onClick={() => toggleMetric(m.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer ${
                activeMetrics.includes(m.key) ? 'text-white' : 'bg-gray-100 text-gray-400'
              }`}
              style={activeMetrics.includes(m.key) ? { backgroundColor: m.color } : {}}
            >
              <span>{m.label}</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-[200px] flex items-center justify-center">
          <div className="text-sm text-gray-400">加载中...</div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <svg
            viewBox={`0 0 ${chartW} ${chartH}`}
            className="w-full"
            style={{ minWidth: '500px', height: `${chartH}px` }}
          >
            {yTicks.map((tick, i) => {
              const y = getY(tick);
              return (
                <g key={i}>
                  <line x1={padL} y1={y} x2={chartW - padR} y2={y} stroke="#f3f4f6" strokeWidth="1" />
                  <text x={padL - 6} y={y + 4} textAnchor="end" fontSize="10" fill="#9ca3af">
                    {tick >= 1000 ? `${(tick / 1000).toFixed(1)}k` : tick}
                  </text>
                </g>
              );
            })}

            {trendData.map((d, i) => {
              if (i % 5 !== 0 && i !== n - 1) return null;
              return (
                <text key={i} x={getX(i)} y={chartH - 4} textAnchor="middle" fontSize="10" fill="#9ca3af">
                  {d.date}
                </text>
              );
            })}

            {metrics.filter((m) => activeMetrics.includes(m.key)).map((m) => (
              <path key={`area-${m.key}`} d={buildArea(m.key)} fill={m.bg} />
            ))}

            {metrics.filter((m) => activeMetrics.includes(m.key)).map((m) => (
              <path
                key={`line-${m.key}`}
                d={buildPath(m.key)}
                fill="none"
                stroke={m.color}
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}

            {trendData.length > 0 && metrics.filter((m) => activeMetrics.includes(m.key)).map((m) => {
              const last = trendData[n - 1];
              return (
                <circle
                  key={`dot-${m.key}`}
                  cx={getX(n - 1)}
                  cy={getY(last[m.key])}
                  r="4"
                  fill={m.color}
                  stroke="white"
                  strokeWidth="2"
                />
              );
            })}
          </svg>
        </div>
      )}

      {!loading && trendData.every((d) => d.pv === 0) && (
        <div className="text-center text-xs text-gray-400 mt-2">
          暂无访问数据，用户访问后将自动记录
        </div>
      )}
    </div>
  );
}
